import React, { useState, useEffect } from 'react';
import { TabbedForm, useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { EventFormToolbar } from './event-utils/EventFormToolbar';
import { EventFormHeader } from './event-utils/EventFormHeader';
import { LazyFormTab } from './event-utils/LazyFormTab';
import { EventTabNavProvider, EventFormTabs } from './event-utils/EventTabNav';
import { EventBasicDetailsTab } from './event-tabs/EventBasicDetailsTab';
import { EventDateDetailsTab } from './event-tabs/EventDateDetailsTab';
import { EventMoreDetailsTab } from './event-tabs/EventMoreDetailsTab';
import { EventVisualsTab } from './event-tabs/EventVisualsTab';
import { EventProductsTab } from './event-tabs/EventProductsTab';
import { EventStockEditTab } from './event-tabs/EventStockEditTab';
import { EventLayoutTab } from './event-tabs/EventLayoutTab';
import { EventCouponsTab } from './event-tabs/EventCouponsTab';
import { EventSettingsTab } from './event-tabs/EventSettingsTab';
import { normalizeEventType, shouldShowLayoutTab } from '../../utils/eventFormUtils';

/**
 * Event V2 Form Component
 * Shared form component for both create and edit operations
 */
export const EventV2Form = ({
  isEdit = false,
  permissions = {},
  record = {},
  cloneRecord = null,
  eventType: initialEventType,
  dateType: initialDateType,
}) => {
  const dataProvider = useDataProvider();
  const [eventType, setEventType] = useState(initialEventType);
  const [dateType, setDateType] = useState(initialDateType);
  const [systemParams, setSystemParams] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSystemParams = async () => {
      try {
        const res = await dataProvider.getOne('systemParameters', { id: 'system' });
        setSystemParams(res.data);
      } catch (error) {
        console.error('Error fetching system parameters:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemParams();
  }, [dataProvider]);

  const handleEventTypeChange = React.useCallback((newEventType) => {
    setEventType(normalizeEventType(newEventType));
  }, []);

  const handleDateTypeChange = React.useCallback((newDateType) => {
    setDateType(newDateType);
  }, []);

  const formProps = React.useMemo(
    () => ({
      isEdit,
      permissions,
      record,
      eventType,
      dateType,
      onEventTypeChange: handleEventTypeChange,
      onDateTypeChange: handleDateTypeChange,
      systemParams,
    }),
    [
      isEdit,
      permissions,
      record,
      eventType,
      dateType,
      handleEventTypeChange,
      handleDateTypeChange,
      systemParams,
    ],
  );

  const formDefaultValues = React.useMemo(() => {
    const vars = systemParams?.variables || {};
    const feeDefaults = {
      commissionFee: vars.defaultCommissionFee != null ? Number(vars.defaultCommissionFee) : 5,
      servicefee: vars.defaultServiceFee != null ? Number(vars.defaultServiceFee) : 0,
    };
    const pickFee = (raw, fallback) => {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
      return fallback;
    };

    if (isEdit && record) {
      return {
        ...record,
        type: record.type ?? eventType,
        dateType: record.dateType ?? dateType ?? 'oneoff',
        commissionFee:
          record.commissionFee != null && record.commissionFee !== ''
            ? record.commissionFee
            : feeDefaults.commissionFee,
        servicefee:
          record.servicefee != null && record.servicefee !== ''
            ? record.servicefee
            : feeDefaults.servicefee,
      };
    }
    if (cloneRecord) {
      return {
        ...cloneRecord,
        type: cloneRecord.type ?? eventType,
        dateType: cloneRecord.dateType ?? dateType,
        commissionFee: pickFee(cloneRecord.commissionFee, feeDefaults.commissionFee),
        servicefee: pickFee(cloneRecord.servicefee, feeDefaults.servicefee),
      };
    }
    if (eventType && dateType) {
      return { type: eventType, dateType, ...feeDefaults };
    }
    return { ...feeDefaults };
  }, [isEdit, record, cloneRecord, eventType, dateType, systemParams]);

  const translate = useTranslate();
  const [locale] = useLocaleState();

  const sourceLang = isEdit ? record?.sourceLanguage || 'tr' : locale || 'tr';

  if (loading) {
    return <div>{translate('ra.page.loading', { _: 'Yükleniyor...' })}</div>;
  }

  const showLayout = shouldShowLayoutTab(eventType);
  const formTabs = [
    <LazyFormTab key="basic" label={translate('form.basic_details', 'Basic Details')}>
      <EventBasicDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="date" label={translate('form.date_details', 'Date Details')}>
      <EventDateDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="more" label={translate('form.more_details', 'Description & Rules')}>
      <EventMoreDetailsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="visuals" label={translate('form.visuals', 'Visuals')}>
      <EventVisualsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="products" label={translate('form.products', 'Products & Stocks')}>
      {isEdit ? <EventStockEditTab {...formProps} /> : <EventProductsTab {...formProps} />}
    </LazyFormTab>,
    ...(showLayout
      ? [
          <LazyFormTab key="layout" label={translate('form.layout', 'Layout')}>
            <EventLayoutTab {...formProps} />
          </LazyFormTab>,
        ]
      : []),
    <LazyFormTab key="coupons" label={translate('form.coupons', 'Coupons')}>
      <EventCouponsTab {...formProps} />
    </LazyFormTab>,
    <LazyFormTab key="settings" label={translate('form.settings', 'Settings & Status')}>
      <EventSettingsTab {...formProps} />
    </LazyFormTab>,
  ];
  const tabCount = formTabs.length;

  return (
    <EventTabNavProvider tabCount={tabCount}>
      <EventFormHeader
        eventType={eventType}
        dateType={dateType}
        eventName={cloneRecord?.name ?? record?.name}
        isEdit={isEdit}
        isCloned={!!cloneRecord?.isCloned}
        record={record}
      />

      {cloneRecord?.isCloned && (
        <div style={{ margin: '0 15px 16px', color: '#b45309', fontWeight: 500 }}>
          {translate('create_wizard.clone_banner', {
            _: 'Cloned event — sessions, products and settings were copied with new IDs. Review dates before publishing. Layout was deep-copied (source event layout is not shared).',
          })}
        </div>
      )}

      <TabbedForm
        warnWhenUnsavedChanges
        shouldUnregister={false}
        syncWithLocation={false}
        id="eventForm"
        defaultValues={formDefaultValues}
        tabs={<EventFormTabs />}
        toolbar={
          <EventFormToolbar
            isEdit={isEdit}
            eventType={eventType}
            dateType={dateType}
            sourceLang={sourceLang}
          />
        }
        sx={{
          width: '100%',
          maxWidth: '100%',
          '& .RaTabbedForm-content': {
            width: '100%',
            maxWidth: '100%',
          },
          '& .MuiTabPanel-root, & [role="tabpanel"]': {
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        {formTabs}
      </TabbedForm>
    </EventTabNavProvider>
  );
};
